import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "./style.css"

interface InventoryTableProps {
    categoryAdjustments?: Record<number, number>;
    onlyRead?: boolean;
    dataTable: IBatchCategoryContent[];
    referenceTable?: IBatchCategoryContent[];
    typeTable: string;
    methods: UseFormReturn;
    viewAdjustment?: boolean;
}

export default function InventoryTable({ onlyRead = false, dataTable, referenceTable = [], typeTable, viewAdjustment = false, methods, categoryAdjustments = {} }: InventoryTableProps) {
    const { t } = useTranslation();
    const categoryOrder = [6, 7, 8, 9, 10, 11, 1, 2, 3, 4, 5];

    const columnLabels = [
        <>0 a 8<br />{t("inventory.months")}</>,
        <>9 a 12<br />{t("inventory.months")}</>,
        <>13 a 24<br />{t("inventory.months")}</>,
        <>25 a 36<br />{t("inventory.months")}</>,
        <>{`> 37 ${t("inventory.months")}`}</>,
        <>{t("inventory.bulls")}</>,
        <>0 a 8<br />{t("inventory.months")}</>,
        <>9 a 12<br />{t("inventory.months")}</>,
        <>13 a 24<br />{t("inventory.months")}</>,
        <>25 a 36<br />{t("inventory.months")}</>,
        <>{`> 37 ${t("inventory.months")}`}</>,
    ];

    interface FixInventoryProps {
        onlyRead?: boolean;
        batchCategory: IBatchCategoryContent[];
        viewAdjustment: boolean;
    }

    const FixInventory: React.FC<FixInventoryProps> = ({ onlyRead = false, batchCategory, viewAdjustment }) => {

        const watchedValues = useWatch({ control: methods.control });

        const getColumnTotal = (categoryId: number) => {
            const batches = (watchedValues && watchedValues[typeTable]) ? watchedValues[typeTable] : {};
            return Object.values(batches).reduce((sum, batch: any) => sum + (batch?.[categoryId] || 0), 0);
        };

        const getRowTotal = (batchIdx: number) => categoryOrder.reduce((sum, catId) => sum + (watchedValues?.[typeTable]?.[batchIdx]?.[catId] || 0), 0);

        return (
            <>
                <tbody>
                    {batchCategory?.map((batch, batchIdx) => {
                        const batchKey = `${batch.farm_initials}-${batch.area_initials}-${batch.initials}`;
                        const rowTotal = getRowTotal(batch.batch_id);
                        const referenceRowTotal = referenceTable.find(reference => reference.batch_id === batch.batch_id)?.categories.reduce((sum, cat) => sum + (cat.quantity - (cat.inventory_adjustment ?? 0)), 0) ?? 0;
                        const adjustment = rowTotal - referenceRowTotal;

                        return (
                            <tr className={batchIdx % 2 === 0 ? "bg_white" : "bg_gray"} key={batch.batch_id}>
                                <th scope="row" style={{ textAlign: "center" }}>
                                    {batchKey}
                                </th>
                                {categoryOrder.map((id) => (
                                    <td key={`${batch.batch_id}.${id}`} style={{ textAlign: "center" }}>
                                        <Controller
                                            name={`${typeTable}.${batch.batch_id}.${id}`}
                                            control={methods.control}
                                            render={({ field }) => (
                                                <input
                                                    name={field.name}
                                                    ref={field.ref}
                                                    className={onlyRead ? "input__inventoryTable" : "inputEditable__inventoryTable"}
                                                    type="number"
                                                    value={field.value === "" || field.value === undefined ? "" : field.value}
                                                    disabled={onlyRead}
                                                    onChange={(event) => {
                                                        const val = event.target.value;
                                                        if (val === "") field.onChange("");
                                                        else {
                                                            const n = Number(val);
                                                            field.onChange(Number.isNaN(n) ? 0 : n);
                                                        }
                                                    }}
                                                    onBlur={() => String(field.value) === "" && field.onChange(0)}
                                                />
                                            )}
                                        />
                                    </td>
                                ))}
                                <td style={{ textAlign: "center" }}>
                                    <input
                                        value={rowTotal}
                                        className={"input__inventoryTable"}
                                        disabled
                                    />
                                </td>
                                {viewAdjustment &&
                                    <td style={{ textAlign: "center" }}>
                                        <input
                                            value={referenceTable ? adjustment : 0}
                                            className={"input__inventoryTable"}
                                            style={{
                                                color: adjustment >= 0 ? "#62B8AE" : "#ff6161"
                                            }}
                                            disabled
                                        />
                                    </td>
                                }
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr style={{ background: "#324440", color: "#fff" }}>
                        <th style={{ textAlign: "center" }}>{t("inventory.total")}</th>
                        {[6, 7, 8, 9, 10, 11, 1, 2, 3, 4, 5].map((id) => (
                            <td key={id} style={{ textAlign: "center" }}>
                                <input
                                    value={Number(getColumnTotal(id))}
                                    className={"input__inventoryTable"}
                                    style={{
                                        color: "#fff"
                                    }}
                                    disabled
                                />
                            </td>
                        ))}
                        <td style={{ textAlign: "center" }}>
                            <input
                                value={dataTable.reduce((sum, batch) => sum + getRowTotal(batch.batch_id), 0)}
                                className={"input__inventoryTable"}
                                style={{
                                    width: 80,
                                    color: "#fff"
                                }}
                                disabled
                            />
                        </td>
                        {viewAdjustment &&
                            <td style={{ textAlign: "center" }}>
                                -
                            </td>
                        }
                    </tr>
                    <tr style={{ background: "#f1f1f1" }}>
                        <th style={{ textAlign: "center" }}>{t("inventory.adjustment")}</th>
                        {[6, 7, 8, 9, 10, 11, 1, 2, 3, 4, 5].map((id) => {
                            const referenceTotal = referenceTable?.reduce((sum, batch) => {
                                const cat = batch.categories.find(cat => cat.id === id);
                                return sum + (cat ? cat.quantity - (cat.inventory_adjustment || 0) : 0);
                            }, 0) ?? 0;

                            const formTotal = Number(getColumnTotal(id));

                            const adjustment = categoryAdjustments[id] ?? 0;

                            const value = viewAdjustment ? (formTotal - referenceTotal) : adjustment;

                            return (
                                <td key={id} style={{ textAlign: "center" }}>
                                    {referenceTable &&
                                        <input
                                            value={value}
                                            className={"input__inventoryTable"}
                                            style={{
                                                color: value >= 0 ? "#62B8AE" : "#ff6161",
                                            }}
                                            disabled
                                        />
                                    }
                                </td>
                            );
                        })}
                        <td style={{ textAlign: "center" }}>
                            {viewAdjustment ? '-' : (
                                <input
                                    value={categoryOrder.reduce((sum, id) => sum + (categoryAdjustments[id] ?? 0), 0)}
                                    className={"input__inventoryTable"}
                                    style={{
                                        color: (
                                            (() => {
                                                const value = categoryOrder.reduce((sum, id) => sum + (categoryAdjustments[id] ?? 0), 0);
                                                return value >= 0 ? "#62B8AE" : "#ff6161";
                                            })()
                                        ),
                                    }}
                                    disabled
                                />
                            )}
                        </td>
                        {viewAdjustment &&
                            <td style={{ textAlign: "center" }}>
                                <input
                                    value={
                                        referenceTable
                                            ? dataTable.reduce((sum, batch) => sum + getRowTotal(batch.batch_id), 0)
                                            - referenceTable.reduce((sum, batch) => sum + batch.categories.reduce((catSum, cat) => catSum + (cat.quantity - (cat.inventory_adjustment ?? 0)), 0), 0)
                                            : dataTable.reduce((sum, batch) => sum + getRowTotal(batch.batch_id), 0)
                                    }
                                    className={"input__inventoryTable"}
                                    style={{
                                        color: (
                                            (() => {
                                                const value = referenceTable
                                                    ? dataTable.reduce((sum, batch) => sum + getRowTotal(batch.batch_id), 0)
                                                    - referenceTable.reduce((sum, batch) => sum + batch.categories.reduce((catSum, cat) => catSum + (cat.quantity - (cat.inventory_adjustment ?? 0)), 0), 0)
                                                    : dataTable.reduce((sum, batch) => sum + getRowTotal(batch.batch_id), 0);
                                                return value >= 0 ? "#62B8AE" : "#ff6161";
                                            })()
                                        ),
                                    }}
                                    disabled
                                />
                            </td>
                        }
                    </tr>
                </tfoot>
            </>
        );
    };

    return (
        <>
            <table style={{
                borderRadius: "10px",
                overflow: "hidden",
                width: viewAdjustment ? 1210 : 1130
            }} >
                <thead style={{ color: "#fff" }}>
                    <tr>
                        <th
                            rowSpan={2}
                            style={{
                                textAlign: "center",
                                background: "#324440",
                                width: 250,
                            }}
                        >
                            {t("inventory.batches")}
                        </th>
                        <th
                            colSpan={6}
                            style={{
                                textAlign: "center",
                                background: "#62b8ae",
                                width: 400,
                            }}
                        >
                            {t("inventory.male")}
                        </th>
                        <th
                            colSpan={5}
                            style={{
                                textAlign: "center",
                                background: "#ff6161",
                                width: 400,
                            }}
                        >
                            {t("inventory.female")}
                        </th>
                        <th
                            rowSpan={2}
                            style={{
                                background: "#57CE71",
                                textAlign: "center",
                                width: 90,
                            }}
                        >
                            {t("inventory.total")}
                        </th>
                        {viewAdjustment && <th
                            rowSpan={2}
                            style={{
                                background: "#324440",
                                textAlign: "center",
                                width: 150,
                            }}
                        >
                            {t("inventory.adjustment")}
                        </th>}
                    </tr>
                    <tr>
                        {columnLabels.map((label, idx) => (
                            <th
                                key={idx}
                                scope="col"
                                style={{
                                    background: "#324440",
                                    textAlign: "center",
                                    width: 85,
                                }}

                            >
                                {label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <FixInventory onlyRead={onlyRead} batchCategory={dataTable} viewAdjustment={viewAdjustment} />
            </table>
        </>
    );
}
